import {
  LayoutDashboard,
  Building2,
  FileText,
  Receipt,
  Users,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  role: string;
}

const menuItems = [
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
    roles: ['admin', 'operator', 'viewer'],
  },
  {
    id: 'company',
    labelKey: 'nav.companyManagement',
    icon: Building2,
    path: '/company',
    roles: ['admin', 'operator', 'viewer'],
  },
  {
    id: 'license',
    labelKey: 'nav.licenseManagement',
    icon: FileText,
    path: '/license',
    roles: ['admin', 'operator', 'viewer'],
  },
  {
    id: 'expense',
    labelKey: 'nav.expenseApplication',
    icon: Receipt,
    path: '/expense',
    roles: ['admin', 'operator'],
  },
  {
    id: 'users',
    labelKey: 'nav.userManagement',
    icon: Users,
    path: '/users',
    roles: ['admin'],
  },
];

export default function Sidebar({ open, onToggle, role }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(role));

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-slate-800 text-white transition-all duration-300 z-50 ${
        open ? 'w-64' : 'w-16'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          {open && <h1 className="text-lg font-bold">BRC System</h1>}
          <button
            onClick={onToggle}
            className="p-1 rounded hover:bg-slate-700 transition-colors"
          >
            {open ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 py-4">
          <ul className="space-y-1 px-2">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const basePath = currentPath.split('?')[0];
              const isActive = basePath === item.path || basePath.startsWith(item.path + '/') || currentPath.startsWith(item.path + '?');
              return (
                <li key={item.id}>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {open && <span className="ml-3 text-sm font-medium">{t(item.labelKey)}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
